import { memo, useEffect, useState } from 'react';
import { ContentPlaceholder } from 'src/core/POS/ContentPlaceholder';
import { vars } from 'src/core/themes';

import * as styles from './Table.css';

/**
 * Deferred row content for virtualized tables (V2 rowWrapperV2 path only).
 *
 * On first mount, renders lightweight shimmer cells matching the table's
 * column widths and cell styles (no hooks, no computation). On the next
 * animation frame, swaps in the real pre-rendered content.
 *
 * This eliminates blank spaces during fast scrolling — the <tr> element appears
 * immediately with correctly-sized shimmer cells, and the real content fills in
 * one frame later with zero layout shift.
 *
 * Note: renderContent is a ReactNode (already evaluated by the caller), not a
 * render function. The deferral only delays when the content is committed to the
 * DOM — the row wrapper (e.g., FormProvider) still mounts synchronously so hooks
 * are initialized immediately.
 */
const DeferredRowContentRaw = ({
  renderContent,
  columnWidths,
}: {
  renderContent: React.ReactNode;
  columnWidths: number[];
}) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!ready) {
    return (
      <>
        {columnWidths.map((width, i) => (
          <td
            key={i}
            className={styles.tableCell}
            style={{ width, minWidth: width, maxWidth: width }}
          >
            <div
              className={styles.tableCellInner}
              style={{ padding: vars.spacing.xs }}
            >
              <ContentPlaceholder />
            </div>
          </td>
        ))}
      </>
    );
  }

  return <>{renderContent}</>;
};

export const DeferredRowContent = memo(DeferredRowContentRaw);
